import React from 'react'
import { Modal, Row } from 'react-bootstrap'
import useSearchGames from '../hooks/useSearchGames'
import GameSearch from '../GameSearch'
import Button, { PrimaryButton } from '../../Common/Button'
import Loader from '../../Loader'
import NoDataAvailable from 'components/NoDataAvailable'
import GameCardFactory from '../../../factory/GameCardFactory'
import './styles/index.scss'
import useOutsideClick from 'utils/useOutsideClick'
import { GAME_CARD_VARIANTS } from '../../../constants/uiConfig'
import Icon from 'icons/Icon'
import { BUTTON_VARIANT } from 'utils/constants'
import GameFiltersSelection from '../GameFiltersSelection'
import SearchDropDownPanel from './SearchDropDownPanel'

/**
 * ModalSearch component to display a modal for searching and filtering games.
 * @param {Object} props - Component props
 * @param {boolean} props.showSearchModal - Flag to control visibility of the modal.
 * @param {Function} props.setShowSearchModal - Function to set visibility of the modal.
 * @returns {JSX.Element} Modal component for game search.
 */
const ModalSearch = ({ showSearchModal, setShowSearchModal }) => {
  const {
    gameSearch,
    selectedSearchGameFilter,
    setSelectedSearchGameFilter,
    languageData,
    toggleFavorite,
    loadMore,
    loadAll,
    hasMore,
    isLoadAllActive,
    bottomRef,
    loading,
    selectedLanguageCode,
    selectedFilter,
    setSelectedFilter,
    general,
    gameFilterSupportType,
    providersWithStudios,
    studios,
    favoriteGamesList
  } = useSearchGames()

  const { ref, isVisible, setIsVisible } = useOutsideClick(false)

  return (
    <Modal show={showSearchModal} centered scrollable className='game-search-modal'>
      <Modal.Header>
        <div className='modal-title'>{languageData?.casinoSearch}</div>
        <Button variant={BUTTON_VARIANT.CONTENT_ONLY} className='close-btn modal-close' onClick={() => setShowSearchModal(false)}>
          <Icon name='xmark' width='28px' height='28px' fill='#ffffff' />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Row className='filter-search-input w-100'>
          <div className='game-search'>
            <GameSearch selectedFilter={selectedSearchGameFilter} setSelectedFilter={setSelectedSearchGameFilter} languageData={languageData} />
          </div>
          <div className='game-provider'>
            <button
              type='button'
              className='multi-select-dropdown-trigger'
              onClick={() => setIsVisible(prev => !prev)}
            >
              <span>{languageData?.providersAndStudios || 'Providers & Studios'}</span>
              <div className='d-flex align-items-center'>
                {selectedFilter?.provider?.length ? <span className='provider-count'>{selectedFilter?.provider?.length}</span> : null}
                <Icon name={isVisible ? 'chevron-up' : 'chevron-down'} width='16' height='16' />
              </div>
            </button>
            {isVisible && (
              <div ref={ref} className='provider-dropdown-panel'>
                <SearchDropDownPanel
                  gameFilterSupportType={gameFilterSupportType}
                  selectedFilter={selectedFilter}
                  setSelectedFilter={setSelectedFilter}
                  languageData={languageData}
                  studios={studios}
                  providersWithStudios={providersWithStudios}
                />
              </div>
            )}
          </div>
        </Row>
        <Row>
          <GameFiltersSelection
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            languageData={languageData}
          />
        </Row>
        <Row className='filter-search-list'>
          <div className='search-game-listing'>
            {gameSearch?.rows?.map((game, index) => (
              <GameCardFactory
                key={index}
                game={game}
                index={index}
                toggleFavorite={toggleFavorite}
                setShowSearchModal={setShowSearchModal}
                selectedLanguageCode={selectedLanguageCode}
                playGameCallback={() => setShowSearchModal(false)}
                hideGameInfo={general?.gameCardVariant === GAME_CARD_VARIANTS.V3}
                favoriteGamesList={favoriteGamesList}
              />
            ))}
            {isLoadAllActive && hasMore && <div ref={bottomRef} style={{ height: '20px' }} />}
            {loading && <Loader top='5%' />}
            {!loading && !gameSearch?.rows?.length && <NoDataAvailable errorMessage={languageData?.casinoNoGamesFound} />}
            {!loading && hasMore && !isLoadAllActive && (
              <div className='search-game-listing__buttons'>
                <PrimaryButton
                  className='mt-4 button'
                  onClick={loadMore}
                  isDisabled={!hasMore}
                  variant={BUTTON_VARIANT.PRIMARY}
                >
                  {languageData?.loadMore?.toUpperCase()}
                </PrimaryButton>
                <PrimaryButton
                  className='mt-4 button'
                  onClick={loadAll}
                  isDisabled={!hasMore}
                  variant={BUTTON_VARIANT.PRIMARY}
                >
                  {languageData?.loadAll?.toUpperCase()}
                </PrimaryButton>
              </div>
            )}
          </div>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export default ModalSearch
